//* 답례품 스토어 메인 배너 — 순수 로직 (docs/gift-store-banner-spec.md)
//* 노출 판정은 서버가 하고(고객 폰 시계 불신), 여기 상태 계산은 관리자 화면 표시용이다.
import {
  GiftStoreBannerData,
  GiftStoreBannerLink,
  GiftStoreBannerLinkType,
} from '@shared/api/giftStoreBanner/giftStoreBanner.interface';
import { fromKstParts, toKstParts } from '@shared/utils/kstDate';

type Ref = string | { _id: string } | null | undefined;

export const refId = (ref: Ref): string | undefined => {
  if (!ref) return undefined;
  return typeof ref === 'object' ? ref._id : ref;
};

// ── 관리자 상태칩 ────────────────────────────────────────────

export type GiftStoreBannerStatus = 'Live' | 'Scheduled' | 'Ended' | 'Off';

export const GIFT_STORE_BANNER_STATUS_LABEL: Record<GiftStoreBannerStatus, string> = {
  Live: '노출중',
  Scheduled: '예약',
  Ended: '종료',
  Off: '꺼짐',
};

export const getGiftStoreBannerStatus = (
  banner: Pick<GiftStoreBannerData, 'isEnabled' | 'startAt' | 'endAt'>,
  now: Date = new Date(),
): GiftStoreBannerStatus => {
  if (banner.isEnabled === false) return 'Off';
  const t = now.getTime();
  if (banner.endAt && new Date(banner.endAt).getTime() <= t) return 'Ended';
  if (banner.startAt && new Date(banner.startAt).getTime() > t) return 'Scheduled';
  return 'Live';
};

// ── datetime-local ↔ 순간값 (항상 KST로 입력·표시) ─────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 순간값 → `YYYY-MM-DDTHH:mm` (KST) */
export const toKstDateTimeInput = (value: Date | string | null | undefined): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = toKstParts(d);
  return `${p.year}-${pad2(p.month)}-${pad2(p.date)}T${pad2(p.hours)}:${pad2(p.minutes)}`;
};

/** `YYYY-MM-DDTHH:mm` (KST) → ISO. 빈 값·형식 오류는 null */
export const fromKstDateTimeInput = (value: string): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  return fromKstParts(y!, mo!, d!, h!, mi!).toISOString();
};

/** 관리자 목록 표시용 `MM.DD HH:mm` (KST) */
export const formatKstShort = (value: Date | string | null | undefined): string => {
  if (!value) return '';
  const p = toKstParts(value);
  return `${p.year}.${pad2(p.month)}.${pad2(p.date)} ${pad2(p.hours)}:${pad2(p.minutes)}`;
};

// ── 스토어 스크롤 위치 (?focus=) ───────────────────────────────
// 배너 클릭은 스크롤을 직접 하지 않고 ?focus= 를 붙인다 → 알림톡 등 외부 링크도 같은 위치로 진입.

export const GIFT_STORE_FOCUS_PARAM = 'focus';

export type GiftStoreFocus =
  | { kind: 'brand'; brandId: string }
  | { kind: 'cat'; categoryId: string }
  | { kind: 'sub'; categoryId: string; sub: string };

export const parseGiftStoreFocus = (value: string | null | undefined): GiftStoreFocus | null => {
  if (!value) return null;
  const sep = value.indexOf(':');
  if (sep <= 0) return null;
  const kind = value.slice(0, sep);
  const rest = value.slice(sep + 1);
  if (!rest) return null;
  if (kind === 'brand') return { kind, brandId: rest };
  if (kind === 'cat') return { kind, categoryId: rest };
  if (kind === 'sub') {
    // 하위분류 이름에 ':'가 들어가도 되도록 첫 구분자만 자른다
    const s = rest.indexOf(':');
    if (s <= 0 || s === rest.length - 1) return null;
    return { kind, categoryId: rest.slice(0, s), sub: rest.slice(s + 1) };
  }
  return null;
};

/** 스크롤형 링크 → focus 값. 스크롤이 아닌 링크(None·원상품 상세)는 null */
export const bannerLinkToFocus = (link: GiftStoreBannerLink | null | undefined): string | null => {
  if (!link) return null;
  if (link.type === GiftStoreBannerLinkType.GiftItemBrand && link.giftItemBrand) {
    return `brand:${link.giftItemBrand}`;
  }
  if (link.type === GiftStoreBannerLinkType.DeliveryCategory && link.giftDeliveryCategory) {
    return link.subCategory
      ? `sub:${link.giftDeliveryCategory}:${link.subCategory}`
      : `cat:${link.giftDeliveryCategory}`;
  }
  return null;
};

// ── 연결 대상이 상조 스토어에 있는가 (관리자 경고용) ─────────────
// 고객 조회에선 검사하지 않는다(무거움). 관리자가 설정할 때 경고로만 막는다.

/** 판매상품 한 건에서 필요한 모양만 (스토어·관리자 목록 공용) */
export type BannerMatchItem = {
  bugoBrand?: Ref;
  giftDeliveryCategory?: Ref; // 상조별 분류 덮어쓰기
  giftItemInfo?: {
    _id?: string;
    isEnabled?: boolean;
    type?: string;
    giftItemBrand?: Ref;
    giftDeliveryCategory?: Ref;
    subCategory?: string | null;
  } | null;
};

/** 이 판매상품이 배너 링크의 대상인가 (스토어 메인의 분류 판정과 같은 규칙) */
export const itemMatchesBannerLink = (
  item: BannerMatchItem,
  link: GiftStoreBannerLink | null | undefined,
): boolean => {
  const info = item.giftItemInfo;
  if (!link || !info || info.isEnabled === false) return false;
  switch (link.type) {
    case GiftStoreBannerLinkType.GiftItemBrand:
      return !!link.giftItemBrand && refId(info.giftItemBrand) === link.giftItemBrand;
    case GiftStoreBannerLinkType.GiftItemInfo:
      return !!link.giftItemInfo && info._id === link.giftItemInfo;
    case GiftStoreBannerLinkType.DeliveryCategory: {
      if (!link.giftDeliveryCategory || info.type === 'Coupon') return false;
      const categoryId =
        refId(item.giftDeliveryCategory) ?? refId(info.giftDeliveryCategory);
      if (categoryId !== link.giftDeliveryCategory) return false;
      return !link.subCategory || info.subCategory === link.subCategory;
    }
    default:
      return false;
  }
};

/** 연결 대상 상품이 하나도 없는 상조 id 목록 (링크 없음이면 빈 배열) */
export const bugoBrandsMissingBannerLink = (
  link: GiftStoreBannerLink | null | undefined,
  bugoBrandIds: string[],
  items: BannerMatchItem[],
): string[] => {
  if (!link || link.type === GiftStoreBannerLinkType.None) return [];
  const covered = new Set<string>();
  items.forEach((item) => {
    const brandId = refId(item.bugoBrand);
    if (brandId && itemMatchesBannerLink(item, link)) covered.add(brandId);
  });
  return bugoBrandIds.filter((id) => !covered.has(id));
};

/** 판매상품이 한 건이라도 있는 상조 id (= 답례품 스토어가 열리는 상조 후보) */
export const bugoBrandIdsWithGiftItems = (items: BannerMatchItem[]): Set<string> => {
  const ids = new Set<string>();
  items.forEach((item) => {
    const id = refId(item.bugoBrand);
    if (id && item.giftItemInfo?.isEnabled !== false) ids.add(id);
  });
  return ids;
};
